import { Button, Stack, Typography } from '@mui/material';
import {
  addCampaignItemsSchema,
  CampaignItemsBaseColumns,
  JoinCentralCampaignDialog,
  LeaveCentralCampaignDialog,
  memberCampaignItemSchema,
  MemberCampaignMetadata,
  useGetMemberCampaignItemsColumns,
} from '@retail/campaign/components';
import {
  MemberCampaign,
  MemberCampaignItem,
  MemberCampaignPatchItemDTO,
} from '@retail/campaign/types';
import { useCallback, useMemo } from 'react';
import { useAppTFunction } from '@retail/app/i18n';
import {
  useMemberCampaignDetailsActions,
  useOverrideCampaignItemPricesActions,
  useUpdateCampaignItemsActions,
} from '@retail/campaign/context';
import { CampaignDetailsContainerBase } from './CampaignDetailsContainerBase';
import { useFetchMemberCampaignItems } from '@retail/campaign/data-access';
import { useTranslation } from 'react-i18next';
import { useDisclosure } from '@shared/hooks';
import { MemberCampaignDetailsAddItemsDialog } from './AddItems';
import { OutlinedButton, UniversalFormProvider } from '@shared/components';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import AddIcon from '@mui/icons-material/Add';
import { ExitToApp } from '@mui/icons-material';
import { MemberEditCampaignItemsDialog } from './MemberEditCampaignItemsDialog';

interface Props {
  campaign: MemberCampaign;
}

export const MemberCampaignDetailsContainer = ({ campaign }: Props) => {
  const t = useAppTFunction();
  const { i18n } = useTranslation();
  const { isOpen: includeVat, onToggle: toggleIncludeVat } = useDisclosure(true);
  const joinCentralCampaignDialog = useDisclosure();
  const leaveCentralCampaignDialog = useDisclosure();

  const {
    data: campaignItemsDto,
    status,
    refetch,
  } = useFetchMemberCampaignItems({
    campaignId: campaign.id,
    campaignType: campaign.campaignType,
    suspense: false,
  });
  const campaignItems = useMemo(
    () => campaignItemsDto?.map((dto) => MemberCampaignItem.fromDto(dto, i18n.language)),
    [campaignItemsDto, i18n.language]
  );

  const { handleUpdateItem } = useUpdateCampaignItemsActions<MemberCampaignPatchItemDTO>({
    campaignId: campaign.id,
    type: 'member',
  });

  const { handleOverrideItemPrice, handleResetItemPrice } = useOverrideCampaignItemPricesActions({
    campaignId: campaign.id,
  });

  const handleSubmitLocalSalesPrice = useCallback(
    (item: MemberCampaignItem, salesPrice: number) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignSalesPrice: salesPrice,
      }),
    [handleUpdateItem]
  );

  const handleSubmitOverrideSalesPrice = useCallback(
    (item: MemberCampaignItem, salesPrice: number) =>
      handleOverrideItemPrice(item.id, { salesPrice }),
    [handleOverrideItemPrice]
  );

  const handleSubmitPurchasePrice = useCallback(
    (item: MemberCampaignItem, purchasePrice: number) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignPurchasePrice: purchasePrice,
      }),
    [handleUpdateItem]
  );

  const handleSubmitCampaignUnit = useCallback(
    (item: MemberCampaignItem, campaignUnit: string) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        campaignUnit,
      }),
    [handleUpdateItem]
  );

  const handleSubmitPurchasePeriod = useCallback(
    (item: MemberCampaignItem, purchasePeriodFrom?: string, purchasePeriodTo?: string) =>
      handleUpdateItem(item.id, {
        ...item.getPatchCampaignItem(),
        purchasePeriodFrom,
        purchasePeriodTo,
      }),
    [handleUpdateItem]
  );

  const {
    isLoadingDistribution,
    isLoadingEnrollCampaign,
    handleChangeCampaignName,
    handleChangeDescription,
    handleChangeCampaignPeriod,
    handleChangePurchasePeriod,
    handleCampaignDistribution,
    handleJoinCampaign,
    handleLeaveCampaign,
  } = useMemberCampaignDetailsActions({ campaign });

  const onJoinCampaign = useCallback(() => {
    handleJoinCampaign().then(joinCentralCampaignDialog.onClose);
  }, [handleJoinCampaign, joinCentralCampaignDialog.onClose]);

  const onLeaveCampaign = useCallback(() => {
    handleLeaveCampaign().then(leaveCentralCampaignDialog.onClose);
  }, [handleLeaveCampaign, leaveCentralCampaignDialog.onClose]);

  const memberCampaignItemsColumns = useGetMemberCampaignItemsColumns({
    includeVat,
    getEditableFields: campaign.editableItemFields,
    handleResetSalesPrice: handleResetItemPrice,
    handleOverrideSalesPrice: handleSubmitOverrideSalesPrice,
  });

  const composeColumns = useCallback(
    (baseColumns: CampaignItemsBaseColumns<MemberCampaignItem>) => {
      return [
        baseColumns.primaryTextColumn,
        baseColumns.mgItemAndExternalNumberColumn,
        baseColumns.supplierColumn,
        baseColumns.primaryPackageUnitColumn,
        baseColumns.campaignUnitColumn,
        ...(campaign.campaignType === 'LOCAL' ? [] : [memberCampaignItemsColumns.isImposedColumn]),
        baseColumns.campaignPurchasePriceColumn,
        memberCampaignItemsColumns.primaryDicountPercentageColumn,
        ...(campaign.campaignType === 'LOCAL'
          ? [baseColumns.campaignSalesPriceColumn]
          : [memberCampaignItemsColumns.campaignSalesPriceColumn]),
        baseColumns.campaignSalesCoverageColumn,
        memberCampaignItemsColumns.localPurchasePriceColumn,
        memberCampaignItemsColumns.localFreightColumn,
        memberCampaignItemsColumns.localCostPriceColumn,
        memberCampaignItemsColumns.localSalesPriceColumn,
        memberCampaignItemsColumns.localSalesCoverageColumn,
        baseColumns.purchasePeriodColumnn,
        baseColumns.createdByColumn,
        ...(campaign.campaignType === 'LOCAL' ? [baseColumns.actionsColumn] : []),
      ];
    },
    [
      campaign.campaignType,
      memberCampaignItemsColumns.campaignSalesPriceColumn,
      memberCampaignItemsColumns.isImposedColumn,
      memberCampaignItemsColumns.localCostPriceColumn,
      memberCampaignItemsColumns.localFreightColumn,
      memberCampaignItemsColumns.localPurchasePriceColumn,
      memberCampaignItemsColumns.localSalesCoverageColumn,
      memberCampaignItemsColumns.localSalesPriceColumn,
      memberCampaignItemsColumns.primaryDicountPercentageColumn,
    ]
  );
  const addItemsSchema = addCampaignItemsSchema(memberCampaignItemSchema(t));

  return (
    <>
      <CampaignDetailsContainerBase
        campaign={campaign}
        campaignDetailsActions={{
          isLoadingDistribution,
          handleCampaignDistribution,
        }}
        type="member"
        campaignItems={campaignItems}
        fetchItemsStatus={status}
        refetchItems={refetch}
        composeItemsColumns={composeColumns}
        includeVat={includeVat}
        toggleIncludeVat={toggleIncludeVat}
        handleSubmitSalesPrice={handleSubmitLocalSalesPrice}
        handleSubmitPurchasePrice={handleSubmitPurchasePrice}
        handleSubmitCampaignUnit={handleSubmitCampaignUnit}
        handleSubmitPurchasePeriod={handleSubmitPurchasePeriod}
        renderMetadata={() => (
          <MemberCampaignMetadata
            campaign={campaign}
            onSaveCampaignName={
              campaign.isFieldEditable('name') ? handleChangeCampaignName : undefined
            }
            onSaveCampaignPeriod={
              campaign.isFieldEditable('campaignPeriod')
                ? (validFrom, validTo) => handleChangeCampaignPeriod({ validFrom, validTo })
                : undefined
            }
            onSavePurchasePeriod={
              campaign.isFieldEditable('purchasePeriod')
                ? (purchasePeriodFrom, purchasePeriodTo) =>
                    handleChangePurchasePeriod({ purchasePeriodFrom, purchasePeriodTo })
                : undefined
            }
            onSaveDescription={
              campaign.isFieldEditable('description') ? handleChangeDescription : undefined
            }
          />
        )}
        renderDistributeDialogContent={() => (
          <Typography textAlign="center">{t('campaign.distribute.description')}</Typography>
        )}
        renderAddItemsDialog={({ onClose, open }) => (
          <UniversalFormProvider schema={addItemsSchema} defaultValues={{ items: {} }}>
            <MemberCampaignDetailsAddItemsDialog
              open={open}
              campaignId={campaign.id}
              onClose={onClose}
            />
          </UniversalFormProvider>
        )}
        renderCTA={({ onOpenDistributeCampaignDialog, onOpenAddItemsDialog }) => {
          if (!campaign.hasGeneralEditPermissions()) return null;
          if (campaign.campaignType === 'LOCAL') {
            return (
              <Stack direction="row" spacing={2}>
                {campaign.releaseStatus !== 'RELEASED' && (
                  <OutlinedButton
                    color="primary"
                    size="small"
                    loading={isLoadingDistribution}
                    startIcon={<PodcastsIcon fontSize="small" />}
                    onClick={onOpenDistributeCampaignDialog}
                  >
                    {t('campaign.actions.distribute')}
                  </OutlinedButton>
                )}
                <Button
                  variant="liquid"
                  size="small"
                  startIcon={<AddIcon fontSize="small" />}
                  onClick={onOpenAddItemsDialog}
                >
                  {t('campaign.actions.addItems')}
                </Button>
              </Stack>
            );
          }
          if (campaign.mandatory) return null;
          if (campaign.enrolled) {
            return (
              <Button
                variant="liquid"
                size="small"
                loading={isLoadingEnrollCampaign}
                startIcon={<ExitToApp fontSize="small" />}
                onClick={leaveCentralCampaignDialog.onOpen}
              >
                {t('campaign.actions.leaveCampaign')}
              </Button>
            );
          }
          return (
            <Button
              variant="liquid"
              size="small"
              loading={isLoadingEnrollCampaign}
              startIcon={<PodcastsIcon fontSize="small" />}
              onClick={joinCentralCampaignDialog.onOpen}
            >
              {t('campaign.actions.joinCampaign')}
            </Button>
          );
        }}
        renderEditCampaignItemsDialog={({ itemsToEdit, onClose }) => (
          <MemberEditCampaignItemsDialog
            campaignItems={itemsToEdit}
            onClose={onClose}
            campaignId={campaign.id}
          />
        )}
      />
      <JoinCentralCampaignDialog
        onJoin={onJoinCampaign}
        onClose={joinCentralCampaignDialog.onClose}
        open={joinCentralCampaignDialog.isOpen}
        isLoading={isLoadingEnrollCampaign}
        campaign={campaign}
      />
      <LeaveCentralCampaignDialog
        onLeave={onLeaveCampaign}
        onClose={leaveCentralCampaignDialog.onClose}
        open={leaveCentralCampaignDialog.isOpen}
        isLoading={isLoadingEnrollCampaign}
        campaign={campaign}
      />
    </>
  );
};
